<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { BaseTable } from 'dashboard/components-next/table';
import IframeAppModal from './IframeAppModal.vue';
import IframeAppRow from './IframeAppRow.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const searchQuery = ref('');
const showModal = ref(false);
const showDeleteConfirmation = ref(false);
const selectedApp = ref({});
const mode = ref('CREATE');

const records = computed(() => store.getters['iframeApps/getRecords']);
const uiFlags = computed(() => store.getters['iframeApps/getUIFlags']);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const tableHeaders = computed(() => [
  t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.TABLE_HEADER.NAME'),
  t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.TABLE_HEADER.URL'),
  t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.TABLE_HEADER.STATUS'),
  t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.TABLE_HEADER.ACTIONS'),
]);

onMounted(() => {
  store.dispatch('iframeApps/get');
});

function openCreatePopup() {
  mode.value = 'CREATE';
  selectedApp.value = {};
  showModal.value = true;
}

function editApp(app) {
  mode.value = 'UPDATE';
  selectedApp.value = app;
  showModal.value = true;
}

function openDeletePopup(app) {
  showDeleteConfirmation.value = true;
  selectedApp.value = app;
}

function closeDeletePopup() {
  showDeleteConfirmation.value = false;
}

function closeModal() {
  showModal.value = false;
  selectedApp.value = {};
}

async function confirmDeletion() {
  closeDeletePopup();
  try {
    await store.dispatch('iframeApps/delete', selectedApp.value.id);
    useAlert(t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.API_SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.API_ERROR'));
  }
}
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="t('INTEGRATION_SETTINGS.IFRAME_APPS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('INTEGRATION_SETTINGS.IFRAME_APPS.TITLE')"
        :description="t('INTEGRATION_SETTINGS.IFRAME_APPS.DESCRIPTION')"
        :search-placeholder="
          t('INTEGRATION_SETTINGS.IFRAME_APPS.SEARCH_PLACEHOLDER')
        "
        feature-name="iframe_apps"
        :back-button-label="t('INTEGRATION_SETTINGS.HEADER')"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{
              t('INTEGRATION_SETTINGS.IFRAME_APPS.COUNT', {
                n: records.length,
              })
            }}
          </span>
        </template>
        <template #actions>
          <NextButton
            :label="t('INTEGRATION_SETTINGS.IFRAME_APPS.HEADER_BTN_TXT')"
            size="sm"
            @click="openCreatePopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredRecords.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ t('INTEGRATION_SETTINGS.IFRAME_APPS.NO_RESULTS') }}
      </span>
      <BaseTable v-else :headers="tableHeaders" :items="filteredRecords">
        <template #row="{ items }">
          <IframeAppRow
            v-for="item in items"
            :key="item.id"
            :app="item"
            @edit="editApp"
            @delete="openDeletePopup"
          />
        </template>
      </BaseTable>
    </template>
    <IframeAppModal
      v-if="showModal"
      :show="showModal"
      :mode="mode"
      :selected-app-data="selectedApp"
      @close="closeModal"
    />
    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.TITLE')"
      :message="
        t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.MESSAGE', {
          appName: selectedApp.name,
        })
      "
      :confirm-text="
        t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.CONFIRM_YES')
      "
      :reject-text="t('INTEGRATION_SETTINGS.IFRAME_APPS.DELETE.CONFIRM_NO')"
    />
  </SettingsLayout>
</template>
